import { RYN_REPLAY_DEFAULT_BLOCK_CLASS } from './ryn-replay-masking-policy';

/**
 * Bug 35675: the web recorder does not mask rendered text by default, so an
 * SSN (full, `XXX-##-####` or last 4) rendered as text in a recorded app is
 * captured as-is. Every such render must sit inside a `ryn-replay-block`
 * element, which rrweb drops from the recording entirely.
 *
 * This is a static guard over RAW Angular template source, run by a spec in
 * each recorded app and shared lib so a new SSN render anywhere fails a test
 * instead of shipping to a recording. It flags an SSN token in:
 * - an interpolation (`{{ dataItem.SSN }}`), or a bound / interpolated
 *   attribute that reaches the DOM (`[title]`, `[cbContent]`, ...);
 * - a Kendo grid column bound by `field` to an SSN property with no cell
 *   template of its own (Kendo renders the value as cell text).
 * unless the element or an ancestor carries the block class.
 *
 * Two DOM facts the walk models:
 * - `kendo-grid-column` renders its `class` on every data cell, so a column
 *   with `class="ryn-replay-block"` blocks its cell template;
 * - a Kendo popup list (dropdown, combobox, ...) is appended to `<body>`,
 *   outside any template ancestor: its item templates count as blocked only
 *   through `popupSettings` `popupClass: 'ryn-replay-block'` (or a class on
 *   the rendered element itself), while its value template renders inside
 *   the host and is blocked by the host's class.
 */
export interface IRynReplaySsnGuardFailure {
  file: string;
  detail: string;
}

/**
 * SSN tokens: `SSN`, `Ssn`, `Last4SSN`, `last4SSN`, `last4ssn`, `ssn`,
 * `ssnMasked`, `socialSecurity...`. Case-aware on purpose: a plain `/ssn/i`
 * matches the `ssN` inside `ClassName`.
 */
export const RYN_REPLAY_SSN_TOKEN_PATTERN =
  /SSN|Ssn|last4ssn|(?:^|[^a-zA-Z])ssn|socialSecurity|SocialSecurity/;

const BLOCK = RYN_REPLAY_DEFAULT_BLOCK_CLASS;
const VOID_ELEMENTS = new Set([
  'input',
  'br',
  'hr',
  'img',
  'link',
  'meta',
  'source',
  'area',
  'base',
  'col',
  'embed',
  'param',
  'track',
  'wbr',
]);
const POPUP_HOSTS = new Set([
  'kendo-dropdownlist',
  'kendo-combobox',
  'kendo-multicolumncombobox',
  'kendo-autocomplete',
  'kendo-multiselect',
]);
/**
 * Bindings that never write the value into the DOM: structural directives,
 * events, two-way form bindings (a native or Kendo text input is covered by
 * `maskAllInputs`), input masks, and the ngx-clipboard `cbContent` payload.
 */
const NON_RENDERING_BINDING =
  /^(\*|\(|\[\(|\[(mask|cbcontent|ngif|ngclass|ngstyle|hidden|disabled|readonly|required|formcontrolname|formcontrol|ngmodel|formfield|field|valuefield|textfield|data|value|filterable|sortable|width|routerlink|ngtemplateoutlet|ngtemplateoutletcontext)\])/i;

const attrRe = /([\w.:@*[\]()-]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'))?/g;

interface IAttr {
  name: string;
  value: string;
}

const parseAttrs = (raw: string): IAttr[] => {
  const out: IAttr[] = [];
  attrRe.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = attrRe.exec(raw))) {
    out.push({ name: m[1], value: m[2] ?? m[3] ?? '' });
  }
  return out;
};

const hasBlockClass = (attrs: IAttr[]): boolean =>
  attrs.some(
    (a) =>
      (a.name === 'class' && new RegExp(`\\b${BLOCK}\\b`).test(a.value)) ||
      a.name === `[class.${BLOCK}]` ||
      (/^\[(class|ngclass)\]$/i.test(a.name) && a.value.includes(BLOCK))
  );

const hasBlockedPopup = (attrs: IAttr[]): boolean =>
  attrs.some(
    (a) => /^\[popupsettings\]$/i.test(a.name) && a.value.includes(BLOCK)
  );

/**
 * A pure string literal (`'SSN'`), or an array of them (a grid toolbar's
 * search-column names), is a fixed label, not a person's value.
 */
const isStringLiteral = (expr: string) =>
  /^\s*'[^']*'\s*$/.test(expr) ||
  /^\s*\[\s*(?:'[^']*'\s*,?\s*)*\]\s*$/.test(expr);

export interface IRynReplaySsnGuardOptions {
  /**
   * Exact interpolation or binding expressions that match the SSN token but
   * render no SSN (a count, a permission flag). Keep each with its reason at
   * the call site.
   */
  allow?: ReadonlySet<string>;
  /**
   * Template-visible names whose value carries an SSN although the name does
   * not say so, e.g. a helper that builds "Last, First (1234)" in TypeScript.
   * Matched as a bare name or call (`getEmployeeDisplayName(`), never as a
   * property of another object.
   */
  ssnBearing?: readonly string[];
}

const GRID_COLUMN_TAGS = new Set([
  'kendo-grid-column',
  'kendo-grid-column-ex',
  'kendo-grid-column-group',
]);
const GROUP_TEMPLATE_ATTR =
  /^kendogrid(group(header|footer)|groupheadercolumn)template$/i;

const hasHeaderBlock = (attrs: IAttr[]): boolean =>
  attrs.some(
    (a) => /^\[?headerclass\]?$/i.test(a.name) && a.value.includes(BLOCK)
  );

/**
 * Walks a raw Angular template and returns every SSN render that is not
 * inside a `ryn-replay-block` element.
 */
export const findUnblockedSsnRenders = (
  html: string,
  file: string,
  options: IRynReplaySsnGuardOptions = {}
): IRynReplaySsnGuardFailure[] => {
  const allow = options.allow ?? new Set<string>();
  // A bare name, never a property access: `row.employeeDisplayName` is a
  // different value from the component's own `employeeDisplayName()`.
  const bearing = (options.ssnBearing ?? []).map(
    (n) =>
      new RegExp(
        `(?:^|[^\\w$.])${n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}${/[\w$]$/.test(n) ? '(?![\\w$])' : ''}`
      )
  );
  const failures: IRynReplaySsnGuardFailure[] = [];
  const source = html.replace(/<!--[\s\S]*?-->/g, '');
  const tagRe = /<(\/?)([a-zA-Z][\w-]*)((?:"[^"]*"|'[^']*'|[^"'>])*)(\/?)>/g;
  const interpRe = /\{\{([\s\S]*?)\}\}/g;
  interface IFrame {
    tag: string;
    blocked: boolean;
    /** Inside a group header/footer template of an SSN-bound column. */
    ssnGroup?: boolean;
    /** On a grid column: its field is an SSN and what blocks it from outside. */
    ssnColumn?: boolean;
    outerBlocked?: boolean;
    /** `[groupable]="false"`: its group templates never render. */
    notGroupable?: boolean;
    /** A field-bound SSN column with no block, reported on close unless a
     * cell template replaced Kendo's own rendering of the field. */
    pendingField?: string;
    /** On a Kendo popup host: whether the host and its popup are blocked. */
    popupHost?: { host: boolean; popup: boolean };
  }
  const stack: IFrame[] = [{ tag: '#root', blocked: false }];
  const top = () => stack[stack.length - 1];
  const blocked = () => top().blocked;
  const flag = (expr: string) =>
    (RYN_REPLAY_SSN_TOKEN_PATTERN.test(expr) ||
      bearing.some((re) => re.test(expr))) &&
    !isStringLiteral(expr) &&
    !allow.has(expr.trim());

  const scanText = (text: string) => {
    if (blocked()) return;
    interpRe.lastIndex = 0;
    let im: RegExpExecArray | null;
    while ((im = interpRe.exec(text))) {
      // In an SSN column's group header the group value IS the SSN,
      // whatever the expression is called (`value`, `dataItem.value`).
      if (top().ssnGroup || flag(im[1])) {
        failures.push({ file, detail: `unblocked interpolation {{${im[1]}}}` });
      }
    }
  };

  const reportField = (tag: string, field: string) =>
    failures.push({
      file,
      detail: `unblocked ${tag} field="${field}" (add class="${BLOCK}" or a blocked cell template)`,
    });

  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = tagRe.exec(source))) {
    const [, closing, rawTag, rawAttrs, selfClosing] = m;
    const tag = rawTag.toLowerCase();
    scanText(source.slice(last, m.index));
    last = tagRe.lastIndex;

    if (closing) {
      const idx = stack.map((s) => s.tag).lastIndexOf(tag);
      if (idx > 0) {
        for (const f of stack.splice(idx)) {
          if (f.pendingField) reportField(f.tag, f.pendingField);
        }
      }
      continue;
    }

    const attrs = parseAttrs(rawAttrs);
    const selfBlocked = blocked() || hasBlockClass(attrs);
    const isGridColumn = GRID_COLUMN_TAGS.has(tag);

    for (const a of attrs) {
      if (NON_RENDERING_BINDING.test(a.name)) continue;
      // A grid column's `class` lands on its data cells; its title renders
      // in the header cell, which only `headerClass` reaches.
      const isHeaderTitle = isGridColumn && /^\[?title\]?$/i.test(a.name);
      const attrBlocked = isHeaderTitle
        ? blocked() || hasHeaderBlock(attrs)
        : selfBlocked;
      if (attrBlocked) continue;
      const bound = /^\[[^\]]+\]$/.test(a.name);
      const interpolated = /\{\{[\s\S]*?\}\}/.test(a.value);
      if ((bound || interpolated) && flag(a.value)) {
        failures.push({
          file,
          detail: `unblocked attribute ${a.name}="${a.value}" on <${tag}>`,
        });
      }
    }

    const field = isGridColumn
      ? (attrs.find((a) => a.name === 'field')?.value ?? '')
      : '';
    const ssnColumn = isGridColumn && flag(field);
    const opensElement = !VOID_ELEMENTS.has(tag) && !selfClosing;
    // Kendo renders a bound field as cell text only when the column has no
    // cell template of its own; that is checked when the column closes.
    if (ssnColumn && !selfBlocked && !opensElement) reportField(tag, field);

    // The group row is not a data cell, so the column class does not reach
    // it: `ggx-group-selection-header` and a custom group template render
    // the group value (the SSN) unless they carry the class themselves.
    if (top().ssnGroup && !selfBlocked && tag.startsWith('ggx-group')) {
      failures.push({
        file,
        detail: `unblocked <${tag}> in an SSN column group header (add class="${BLOCK}")`,
      });
    }

    if (opensElement) {
      const column = [...stack]
        .reverse()
        .find((f) => f.ssnColumn !== undefined);
      const isGroupTemplate =
        tag === 'ng-template' &&
        attrs.some((a) => GROUP_TEMPLATE_ATTR.test(a.name));
      if (
        column &&
        tag === 'ng-template' &&
        attrs.some((a) => /^kendogridcelltemplate$/i.test(a.name))
      ) {
        column.pendingField = undefined;
      }
      if (isGroupTemplate && column?.ssnColumn) {
        // A column that cannot be grouped never renders its group template.
        const unreachable = !!column.outerBlocked || !!column.notGroupable;
        stack.push({ tag, blocked: unreachable, ssnGroup: !unreachable });
      } else {
        const isPopupHost = POPUP_HOSTS.has(tag);
        const parentHost = top().popupHost;
        let childBlocked = isPopupHost
          ? selfBlocked && hasBlockedPopup(attrs)
          : selfBlocked;
        if (parentHost && tag === 'ng-template') {
          const inHost = attrs.some((a) =>
            /(value|tag)template$/i.test(a.name)
          );
          childBlocked = inHost ? parentHost.host : parentHost.popup;
        }
        stack.push({
          tag,
          blocked: childBlocked,
          ...(isPopupHost
            ? {
                popupHost: { host: selfBlocked, popup: hasBlockedPopup(attrs) },
              }
            : {}),
          ssnGroup: top().ssnGroup && !childBlocked,
          ...(isGridColumn
            ? {
                ssnColumn,
                outerBlocked: blocked(),
                notGroupable: attrs.some(
                  (a) =>
                    /^\[groupable\]$/i.test(a.name) &&
                    a.value.trim() === 'false'
                ),
                pendingField: ssnColumn && !selfBlocked ? field : undefined,
              }
            : {}),
        });
      }
    }
  }
  scanText(source.slice(last));
  for (const f of stack) if (f.pendingField) reportField(f.tag, f.pendingField);
  return failures;
};
